import { Button, Flex, Form, Input, Switch, Typography, message, Modal, Select, SelectProps } from "antd";
import { useForm } from "antd/es/form/Form";
import { useBuyPackageByPackageId } from "hooks/mutations/package";
import { useConvertReferralFirstPurchase } from "hooks/mutations/referral";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import { validateDiscountCode } from "hooks/queries/discount-code";
import { useCustomerReferral } from "hooks/queries/referral";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useEffect, useMemo, useState } from "react";
import { translate, translateWithVars } from "shared/languages";
import {
  ClassCategory,
  DiscountCodeValidation,
  LoyaltyDiscountMode,
  LoyaltyDiscountScope,
  LoyaltyRewardType,
  PackageProduct,
  ProductType,
  TaxMode,
} from "shared/types";
import { formatCurrency, formatTierValidity } from "shared/utils";
import { localizeRewardSummary } from "shared/utils/loyalty";
import { applyPurchaseTax } from "shared/utils/tax";
import { colors } from "shared/theme/color";
import { ValidityStartScope, ExpiryUnit } from "shared/types/enum";
import { textPx } from "shared/constants/zoom";
import { InclusiveTaxNote } from "components/InclusiveTaxNote.component";

interface BuyPackageModalProps {
  customerId: string;
  open: boolean;
  onCancel: () => void;
  isMembership?: boolean;
  // Inline-purchase mode (schedule drawer): the class being scheduled is fixed,
  // so the class field is preselected and locked.
  lockedClassId?: string;
  // Show memberships AND credit packs together (both are valid credit sources
  // for a private schedule), grouped with memberships first.
  includeMemberships?: boolean;
  // Fires with the newly created customer_package before the modal closes, so
  // the caller can auto-select it as the slot's credit source.
  onPurchased?: (customerPackage: { id: string; customer_id: string }) => void;
}

// Buying is now a product -> tier cascade: pick the catalog product, then one
// of its credit tiers. Memberships have exactly one tier, so selecting one
// skips the tier step. The final mutation still posts the TIER id
// (/v1/package/buy/:package_id) — the server-side purchase flow is unchanged.
//
// REFERRAL: when the client carries a pending referral (state-driven — this
// fires whether the modal opened straight after registration or days later),
// the modal shows the intro offer and submits through the referral convert
// endpoint instead, so the discount and the conversion land in one
// transaction. The plain-buy post-commit hook stays the safety net for every
// other purchase path.
const BuyPackageModal = ({
  customerId,
  open,
  onCancel,
  isMembership = false,
  lockedClassId,
  includeMemberships = false,
  onPurchased,
}: BuyPackageModalProps) => {
  const [form] = useForm();
  const [selectedClass, setSelectedClass] = useState<string>();
  const [selectedProductId, setSelectedProductId] = useState<string>();
  // Default ON: applying the promised intro discount is the expected path.
  const [applyIntro, setApplyIntro] = useState(true);
  // Kode Diskon. `applied` holds the SERVER's verdict + prices; the UI never
  // computes a discount itself.
  const [codeInput, setCodeInput] = useState("");
  const [applied, setApplied] = useState<DiscountCodeValidation | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const { data: classes, isLoading: classLoading } = getAllClassTypeByBusinessId();
  const { data: productList, isLoading: productLoading } =
    getAllProductsByBusinessId(
      { class_type_id: selectedClass },
      { enabled: !!selectedClass },
    );
  const { data: paymentMethods = [] } = getAllPaymentMethods();
  const paymentMethodOptions = useMemo(
    () => paymentMethods.map((method) => ({ label: method.name, value: method.id })),
    [paymentMethods],
  );
  const { data: customerReferral } = useCustomerReferral(customerId, open);
  const pendingIntro = customerReferral?.pending_intro ?? null;

  // Additive tax adds to whatever pre-tax price is currently in play here
  // (plain tier price, a discount code's final_price, or the referral
  // intro's discountedPrice below) — a live preview against the business's
  // CURRENT settings, same as the Upgrade modal's "to pay" preview.
  // Inclusive tax / tax off never change what's charged, so no breakdown is
  // shown for those — the plain price already IS the total.
  const { data: businessSettings } = useBusinessSettings();
  const isAdditiveTax =
    !!businessSettings?.tax_enabled &&
    businessSettings.tax_mode === TaxMode.Additive &&
    !!businessSettings.tax_rate;
  const withTax = (price: number) =>
    businessSettings ? applyPurchaseTax(price, businessSettings) : price;

  useEffect(() => {
    if (open && lockedClassId) {
      setSelectedClass(lockedClassId);
      form.setFieldsValue({ class: lockedClassId });
    }
  }, [open, lockedClassId, form]);

  // Expired promos are purchase-blocked server-side; hide them here too.
  const sellableProducts = useMemo(
    () =>
      (productList?.products ?? []).filter(
        (product) => !product.is_promo || product.promo_active,
      ),
    [productList],
  );
  const productById = useMemo(
    () => new Map(sellableProducts.map((product) => [product.id, product])),
    [sellableProducts],
  );
  const selectedProduct = selectedProductId
    ? productById.get(selectedProductId)
    : undefined;

  const toProductOption = (product: PackageProduct) => ({
    label: product.is_promo
      ? `${product.name} (${translate("product_catalog.promo_chip")})`
      : product.name,
    value: product.id,
  });
  const productOptions = useMemo<SelectProps["options"]>(() => {
    if (!includeMemberships) {
      return sellableProducts
        .filter(
          (product) =>
            product.product_type ===
            (isMembership ? ProductType.Membership : ProductType.Credit),
        )
        .map(toProductOption);
    }
    const memberships = sellableProducts
      .filter((product) => product.product_type === ProductType.Membership)
      .map(toProductOption);
    const credit = sellableProducts
      .filter((product) => product.product_type === ProductType.Credit)
      .map(toProductOption);
    const groups = [];
    if (memberships.length > 0) {
      groups.push({
        label: translate("membership"),
        title: translate("membership"),
        options: memberships,
      });
    }
    if (credit.length > 0) {
      groups.push({
        label: translate("package"),
        title: translate("package"),
        options: credit,
      });
    }
    return groups;
  }, [sellableProducts, includeMemberships, isMembership]);

  const tierOptions = useMemo<SelectProps["options"]>(() => {
    if (!selectedProduct) return [];
    return selectedProduct.tiers.map((tier) => ({
      label: tier.unlimited
        ? `${translate("membership")} · ${formatCurrency(tier.price)} · ${formatTierValidity(tier)}`
        : `${tier.total_credits} ${translate("product_catalog.sesi")} · ${formatCurrency(tier.price)} · ${formatTierValidity(tier)}`,
      value: tier.id,
    }));
  }, [selectedProduct]);

  // The tier the admin picked (the hidden `package` field carries its id),
  // resolved to its full row for price/unlimited.
  const selectedTierId = Form.useWatch("package", form) as number | undefined;
  const selectedTier = useMemo(
    () =>
      selectedProduct?.tiers.find((tier) => tier.id === selectedTierId) ??
      null,
    [selectedProduct, selectedTierId],
  );

  // Masa Berlaku Produk: at the point of sale, say when this tier's validity
  // will run from the client's first class rather than from today, and by
  // when they must come (the studio's activation deadline). Null = today's
  // purchase-dated rule applies.
  const firstUseDeadline = useMemo(() => {
    if (!businessSettings?.validity_start_first_use || !selectedTier) return null;
    const scope = businessSettings.validity_start_scope;
    const applies =
      scope === ValidityStartScope.All ||
      (scope === ValidityStartScope.Membership
        ? selectedTier.unlimited
        : !selectedTier.unlimited);
    if (!applies) return null;
    const unitKey =
      businessSettings.auto_start_unit === ExpiryUnit.Day
        ? "class_settings.unit.days"
        : "class_settings.unit.months";
    return `${businessSettings.auto_start_value} ${translate(unitKey)}`;
  }, [businessSettings, selectedTier]);

  // Client-side mirror of the server's intro-discount gates (display only —
  // the convert endpoint re-validates everything): friend reward is a
  // discount, the tier is in its scope, and the tier meets the program
  // minimum (compared against the BASE price).
  const friendReward = pendingIntro?.friend_reward ?? null;
  const meetsMinimum =
    !!selectedTier &&
    (pendingIntro?.min_purchase_amount == null ||
      selectedTier.price >= pendingIntro.min_purchase_amount);
  const tierInDiscountScope = useMemo(() => {
    if (!friendReward || !selectedTier || !selectedProduct) return false;
    if (friendReward.reward_type !== LoyaltyRewardType.Discount) return false;
    switch (friendReward.discount_scope) {
      case LoyaltyDiscountScope.Packages:
        return !selectedTier.unlimited;
      case LoyaltyDiscountScope.Memberships:
        return selectedTier.unlimited;
      case LoyaltyDiscountScope.Products:
        return friendReward.product_ids.includes(selectedProduct.id);
      default:
        return true; // 'all'
    }
  }, [friendReward, selectedTier, selectedProduct]);
  const discountAvailable = !!pendingIntro && tierInDiscountScope && meetsMinimum;
  // The intro discount is being applied to THIS sale: only then does the
  // purchase go through the referral convert endpoint (which takes no code).
  // Every other sale of a pending-referral client is a plain buy, which
  // converts the referral post-commit and may carry a discount code
  // (System Review R7; the two never stack).
  const introApplied = discountAvailable && applyIntro;
  // A code typed but never applied would be silently ignored at Buy (R12):
  // the button waits until it is applied or cleared.
  const typedCode = codeInput.trim();
  const codeUnapplied =
    !introApplied &&
    typedCode.length > 0 &&
    !(applied?.valid && applied.code === typedCode);
  const discountedPrice =
    discountAvailable && friendReward && selectedTier
      ? friendReward.discount_mode === LoyaltyDiscountMode.Percent
        ? Math.round(
            selectedTier.price * (1 - (friendReward.discount_value ?? 0) / 100),
          )
        : Math.max(0, selectedTier.price - (friendReward.discount_value ?? 0))
      : null;

  const { mutate: buyPackage, isPending } = useBuyPackageByPackageId();
  const { mutate: convertReferral, isPending: isConverting } =
    useConvertReferralFirstPurchase();
  const closeAfterPurchase = (customerPackage: {
    id: string;
    customer_id: string;
  }) => {
    onPurchased?.(customerPackage);
    onCancel();
    form.resetFields();
    setSelectedProductId(undefined);
    setApplyIntro(true);
    setCodeInput("");
    setApplied(null);
  };
  // Re-check on demand against the CURRENTLY selected tier: the same code can
  // be valid for one tier and out of scope for another, so a tier change must
  // clear a previously applied code rather than silently carry it over.
  const onApplyCode = async () => {
    const selected = form.getFieldValue("package");
    if (!codeInput.trim() || !selected) return;
    setCheckingCode(true);
    try {
      const result = await validateDiscountCode({
        code: codeInput,
        packageId: Number(selected),
        customerId,
      });
      setApplied(result);
      if (!result.valid) {
        message.error(translate(`discountCodes.reason.${result.reason}`));
      }
    } catch {
      message.error(translate("messages.error.general"));
    } finally {
      setCheckingCode(false);
    }
  };

  const clearAppliedCode = () => {
    setApplied(null);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        // Close on success (not optimistically): a failed purchase keeps the
        // modal open with the error toast so the admin can retry.
        if (introApplied) {
          // Intro discount on: record + convert in one transaction so the
          // discounted price and the conversion can't drift apart.
          convertReferral(
            {
              packageId: values.package,
              customerId,
              applyIntroDiscount: true,
              // The field is shown for every purchase; this branch used to
              // drop it, so a referred client's purchase never recorded one.
              paymentMethodId: values.payment_method,
            },
            {
              onSuccess: (result) => {
                message.success(
                  result.converted
                    ? translate("referral.toast.converted")
                    : result.below_minimum
                      ? translate("referral.toast.below_minimum")
                      : translate("messages.success.buy_package"),
                );
                closeAfterPurchase(result.customer_package);
              },
            },
          );
          return;
        }
        buyPackage(
          {
            packageId: values.package,
            customerId,
            paymentMethodId: values.payment_method,
            // Only send a code the server already accepted for THIS tier; it
            // re-resolves anyway, so this just avoids a pointless rejection.
            discountCode:
              applied?.valid ? applied.code : undefined,
          },
          {
            onSuccess: ({ data: response }) => {
              closeAfterPurchase(response.data);
            },
          }
        );
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
      });
  };

  const handleClassTypeChange = (value: string) => {
    setSelectedClass(value);
    setSelectedProductId(undefined);
    form.setFieldsValue({ product: undefined, package: undefined });
    // Same reasoning as the tier Select's own onChange={clearAppliedCode}:
    // this resets `package` too, so any already-applied code's preview
    // (prices, the original tier it was validated against) is now stale.
    clearAppliedCode();
  };

  const handleProductChange = (value: string) => {
    setSelectedProductId(value);
    const product = productById.get(value);
    // A membership has exactly one tier: select it implicitly and skip the
    // tier step.
    form.setFieldsValue({
      package:
        product?.product_type === ProductType.Membership
          ? product.tiers[0]?.id
          : undefined,
    });
    // `package` just changed here too (either cleared, or auto-selected to a
    // DIFFERENT tier for a membership) — same staleness as changing product
    // via the class-type filter above, so the same reset applies.
    clearAppliedCode();
  };

  const showTierStep =
    !!selectedProduct && selectedProduct.product_type === ProductType.Credit;

  const classOptions = useMemo(() => {
    if (!classes) return [];

    const privateClasses = classes.filter((cls) => cls.type === ClassCategory.Private);
    const openClasses = classes.filter((cls) => cls.type === ClassCategory.Open);

    const options = [];

    if (privateClasses.length > 0) {
      options.push({
        label: "Private",
        title: "Private",
        options: privateClasses.map((cls) => ({
          label: cls.name,
          value: cls.id,
        })),
      });
    }

    if (openClasses.length > 0) {
      options.push({
        label: "Open",
        title: "Open",
        options: openClasses.map((cls) => ({
          label: cls.name,
          value: cls.id,
        })),
      });
    }

    return options;
  }, [classes]);

  return (
    <Modal
      // The title names what is actually on offer: both kinds together are
      // "Beli Produk", a single kind keeps its own name.
      title={translate(
        includeMemberships
          ? "title.buy_product"
          : isMembership
            ? "title.buy_membership"
            : "title.buy_package"
      )}
      open={open}
      onOk={onSubmit}
      okButtonProps={{ disabled: codeUnapplied }}
      confirmLoading={isPending || isConverting}
      onCancel={() => {
        onCancel();
        form.resetFields();
        setSelectedProductId(undefined);
        setApplyIntro(true);
      }}
      okText={translate("button.buy")}
    >
      <Form form={form} layout="vertical" name="buyPackageForm">
        <Form.Item
          name="class"
          label={translate("label.class_name")}
          rules={[{ required: true, message: `${translate("form.error.select")} ${translate("class")}` }]}
        >
          <Select
            placeholder={`${translate("form.placeholders.select")} ${translate("label.class_name")}`}
            loading={classLoading}
            onChange={handleClassTypeChange}
            disabled={!!lockedClassId}
            showSearch
            optionFilterProp="label"
            options={classOptions}
          />
        </Form.Item>
        <Form.Item
          name="product"
          label={translate("product_catalog.label_product")}
          rules={[{ required: true, message: translate("form.error.buy_package") }]}
        >
          <Select
            placeholder={translate("product_catalog.select_product")}
            loading={productLoading}
            disabled={!selectedClass}
            onChange={handleProductChange}
            showSearch
            optionFilterProp="label"
            options={productOptions}
          />
        </Form.Item>
        {/* The hidden `package` field carries the tier id for memberships. */}
        <Form.Item
          name="package"
          label={translate("product_catalog.label_tier")}
          hidden={!showTierStep}
          rules={[{ required: true, message: translate("form.error.buy_package") }]}
        >
          <Select
            placeholder={translate("product_catalog.select_tier")}
            disabled={!showTierStep}
            options={tierOptions}
            // A code valid for one tier may be out of scope for another, so a
            // tier change invalidates any preview already on screen.
            onChange={clearAppliedCode}
          />
        </Form.Item>
        {firstUseDeadline && (
          <Typography.Text
            type="secondary"
            style={{ display: "block", marginTop: -12, marginBottom: 16, fontSize: textPx(12.5) }}
          >
            {translateWithVars("class_settings.validity_start.sale_hint", {
              n: firstUseDeadline,
            })}
          </Typography.Text>
        )}
        {/* Deliberately optional — no `required` rule. Recording who paid how
            is useful but shouldn't add a mandatory step to every purchase,
            including the streamlined inline-purchase during schedule
            creation (this same modal, lockedClassId mode). Can be set later
            from the purchase's row in Pembelian Paket/Membership or the
            client's own Paket/Membership tab. */}
        <Form.Item name="payment_method" label={translate("paymentMethods.name")}>
          <Select
            allowClear
            placeholder={translate("paymentMethods.select_placeholder")}
            options={paymentMethodOptions}
          />
        </Form.Item>
      </Form>

      {/* Kode Diskon ALWAYS comes first, breakdown ALWAYS renders below it in
          the same grey box — whether or not a code is applied — rather than
          the box appearing/disappearing and swapping position depending on
          discount state. NO STACKING: a pending referral intro already
          discounts this purchase and goes through its own endpoint, so the
          code input is withheld entirely rather than offered and rejected.
          The server enforces the same rule, this is just the honest
          affordance. */}
      {!introApplied && (
        <div style={{ marginTop: 4 }}>
          <Typography.Text strong style={{ fontSize: textPx(13) }}>
            {translate("discountCodes.name")}
          </Typography.Text>
          <Flex gap={8} style={{ marginTop: 6 }}>
            <Input
              value={codeInput}
              onChange={(e) => {
                // Uppercase the actual value as typed (not just a CSS
                // textTransform, which only changes how it LOOKS while the
                // real state stays whatever case was typed) — matches the
                // server's own normalization and how the code prints on the
                // studio's discount-code list.
                setCodeInput(e.target.value.toUpperCase());
                // Editing invalidates the previous verdict.
                if (applied) setApplied(null);
              }}
              onPressEnter={onApplyCode}
              placeholder={translate("discountCodes.input_placeholder")}
              allowClear
            />
            <Button
              onClick={onApplyCode}
              loading={checkingCode}
              disabled={!codeInput.trim()}
            >
              {translate("discountCodes.apply")}
            </Button>
          </Flex>
          {codeUnapplied && (
            <Typography.Text type="secondary" style={{ fontSize: textPx(12), display: "block", marginTop: 4 }}>
              {translate("discountCodes.apply_first")}
            </Typography.Text>
          )}

          {/* Breakdown box — always shown once a tier is picked, grey
              background regardless of whether a code is applied. With a
              code: original struck through, the discount, then the final
              (mirroring RedeemDiscountVoucherModal). Without one: just the
              plain price. Either way, additive tax adds Pajak/Total rows;
              inclusive/off show only the flat amount. */}
          {selectedTier && (
            <div
              style={{
                marginTop: 10,
                padding: "12px 14px",
                borderRadius: 10,
                background: colors.bgSoft,
                border: `1px solid ${colors.line}`,
              }}
            >
              {applied?.valid ? (
                <>
                  <Flex justify="space-between">
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {translate("discountCodes.original_price")}
                    </Typography.Text>
                    <Typography.Text delete type="secondary" style={{ fontSize: textPx(13) }}>
                      {formatCurrency(applied.original_price)}
                    </Typography.Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginTop: 4 }}>
                    {/* Label stays grey/secondary like every other component
                        row; the amount keeps its green highlight — that
                        color is a deliberate "you saved money" callout, not
                        part of the grey/bold hierarchy. */}
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {applied.code}
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: textPx(13), color: colors.successDark }}>
                      &minus; {formatCurrency(applied.discount_amount)}
                    </Typography.Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginTop: 6 }}>
                    {/* Not the true final amount once additive tax also
                        applies on top — relabeled "Subtotal" and demoted to
                        a regular-weight, grey component row in that case
                        (matching Harga Normal/PPN), with Pajak/Total added
                        below as the actual bold black final rows. Without
                        tax, this genuinely IS the final amount, so it keeps
                        full bold/black treatment like Total elsewhere. */}
                    <Typography.Text
                      strong={!isAdditiveTax}
                      type={isAdditiveTax ? "secondary" : undefined}
                      style={isAdditiveTax ? { fontSize: textPx(13) } : undefined}
                    >
                      {translate(isAdditiveTax ? "pdf.subtotal" : "discountCodes.final_price")}
                    </Typography.Text>
                    <Typography.Text
                      strong={!isAdditiveTax}
                      type={isAdditiveTax ? "secondary" : undefined}
                      style={isAdditiveTax ? { fontSize: textPx(13) } : undefined}
                    >
                      {formatCurrency(applied.final_price)}
                    </Typography.Text>
                  </Flex>
                  <InclusiveTaxNote
                    amount={applied.final_price}
                    enabled={businessSettings?.tax_enabled}
                    mode={businessSettings?.tax_mode}
                    rate={businessSettings?.tax_rate}
                    label={businessSettings?.tax_label}
                  />
                  {isAdditiveTax && (
                    <>
                      <Flex justify="space-between" style={{ marginTop: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                          {businessSettings?.tax_label} ({businessSettings?.tax_rate}%)
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                          {formatCurrency(withTax(applied.final_price) - applied.final_price)}
                        </Typography.Text>
                      </Flex>
                      <Flex justify="space-between" style={{ marginTop: 6 }}>
                        <Typography.Text strong>
                          {translate("analyticsPage.class.total_label")}
                        </Typography.Text>
                        <Typography.Text strong>
                          {formatCurrency(withTax(applied.final_price))}
                        </Typography.Text>
                      </Flex>
                    </>
                  )}
                </>
              ) : isAdditiveTax ? (
                <>
                  <Flex justify="space-between">
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {translate("pdf.subtotal")}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {formatCurrency(selectedTier.price)}
                    </Typography.Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {businessSettings?.tax_label} ({businessSettings?.tax_rate}%)
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {formatCurrency(withTax(selectedTier.price) - selectedTier.price)}
                    </Typography.Text>
                  </Flex>
                  <Flex justify="space-between" style={{ marginTop: 6 }}>
                    <Typography.Text strong>
                      {translate("analyticsPage.class.total_label")}
                    </Typography.Text>
                    <Typography.Text strong>
                      {formatCurrency(withTax(selectedTier.price))}
                    </Typography.Text>
                  </Flex>
                </>
              ) : (
                <>
                  <Flex justify="space-between">
                    <Typography.Text strong>
                      {translate("analyticsPage.class.total_label")}
                    </Typography.Text>
                    <Typography.Text strong>{formatCurrency(selectedTier.price)}</Typography.Text>
                  </Flex>
                  <InclusiveTaxNote
                    amount={selectedTier.price}
                    enabled={businessSettings?.tax_enabled}
                    mode={businessSettings?.tax_mode}
                    rate={businessSettings?.tax_rate}
                    label={businessSettings?.tax_label}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pending-referral intro offer. The price breakdown is display only —
          the convert endpoint computes the authoritative discounted price. */}
      {pendingIntro && (
        <div
          style={{
            marginTop: 4,
            padding: "12px 14px",
            borderRadius: 10,
            background: colors.bgSoft,
            border: `1px solid ${colors.line}`,
          }}
        >
          <Typography.Text strong style={{ fontSize: textPx(13) }}>
            {translate("referral.intro.title")} ·{" "}
            {pendingIntro.referrer_name}
          </Typography.Text>
          {discountAvailable && friendReward && selectedTier ? (
            <>
              <Flex
                justify="space-between"
                align="center"
                gap={12}
                style={{ marginTop: 8 }}
              >
                <Typography.Text style={{ fontSize: textPx(13) }}>
                  {translate("referral.intro.apply_discount")}
                  {pendingIntro.friend_offer
                    ? ` (${localizeRewardSummary(pendingIntro.friend_offer)})`
                    : ""}
                </Typography.Text>
                <Switch
                  size="small"
                  checked={applyIntro}
                  onChange={setApplyIntro}
                  // No stacking: while a code is applied the intro stays off.
                  disabled={!!applied?.valid}
                  aria-label={translate("referral.intro.apply_discount")}
                />
              </Flex>
              {applyIntro && (
                <>
                  <Flex gap={8} align="baseline" style={{ marginTop: 6 }}>
                    <Typography.Text
                      delete
                      type="secondary"
                      style={{ fontSize: textPx(13) }}
                    >
                      {formatCurrency(selectedTier.price)}
                    </Typography.Text>
                    {/* Not the true final amount once additive tax also
                        applies on top — demoted to a regular-weight, grey
                        component row in that case, matching the discount
                        code box's identical treatment. */}
                    <Typography.Text
                      strong={!isAdditiveTax}
                      type={isAdditiveTax ? "secondary" : undefined}
                      style={isAdditiveTax ? { fontSize: textPx(13) } : undefined}
                    >
                      {formatCurrency(discountedPrice ?? 0)}
                    </Typography.Text>
                  </Flex>
                  {/* Outside the additive block below, like the discount-code
                      box: the note is for INCLUSIVE tax, the breakdown for
                      additive, and the two never print together. */}
                  <InclusiveTaxNote
                    amount={discountedPrice ?? 0}
                    enabled={businessSettings?.tax_enabled}
                    mode={businessSettings?.tax_mode}
                    rate={businessSettings?.tax_rate}
                    label={businessSettings?.tax_label}
                  />
                  {isAdditiveTax && (
                    <>
                      <Flex justify="space-between" style={{ marginTop: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                          {businessSettings?.tax_label} ({businessSettings?.tax_rate}%)
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                          {formatCurrency(withTax(discountedPrice ?? 0) - (discountedPrice ?? 0))}
                        </Typography.Text>
                      </Flex>
                      <Flex justify="space-between" style={{ marginTop: 6 }}>
                        <Typography.Text strong>
                          {translate("analyticsPage.class.total_label")}
                        </Typography.Text>
                        <Typography.Text strong>
                          {formatCurrency(withTax(discountedPrice ?? 0))}
                        </Typography.Text>
                      </Flex>
                    </>
                  )}
                </>
              )}
            </>
          ) : selectedTier && !meetsMinimum ? (
            <div style={{ marginTop: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                {translate("referral.intro.below_minimum")}
              </Typography.Text>
            </div>
          ) : selectedTier &&
            friendReward?.reward_type === LoyaltyRewardType.Discount &&
            !tierInDiscountScope ? (
            // The purchase still converts and still issues the friend's
            // voucher; it just can't be redeemed against THIS product.
            <div style={{ marginTop: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                {translate("referral.intro.discount_out_of_scope")}
              </Typography.Text>
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                {translate("referral.intro.first_purchase_note")}
              </Typography.Text>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BuyPackageModal;
